import { useEffect, useState } from 'react'

import { isEmpty } from 'lodash'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import helpers from 'helpers'

const SAVING_STATE = 'saving'
const SAVED_STATE = 'saved'

// Orquestra as respostas de um projeto de Privacy by Design. O service é
// injetado (service.dponet.privacyByDesign ou service.guestPortal.privacyByDesign),
// que expõem a mesma assinatura, então app e portal compartilham este fluxo.
const usePrivacyByDesignAnswers = ({ privacyByDesignId, service }) => {
  const [questions, setQuestions] = useState([])
  const [evidencesByAnswerId, setEvidencesByAnswerId] = useState({})
  const [saveStateByQuestionId, setSaveStateByQuestionId] = useState({})

  const snackbar = useSnackbar()

  const { ANSWERED_STATUS } = constants.privacyByDesignAnswer

  const setSaveState = (questionId, state) =>
    setSaveStateByQuestionId((previous) => ({
      ...previous,
      [questionId]: state,
    }))

  // Carrega as questões do projeto recém-buscado, junto com o mapa de evidências.
  const loadPrivacyByDesign = (privacyByDesign) => {
    setQuestions(privacyByDesign?.questions || [])
    setEvidencesByAnswerId(
      helpers.privacyByDesign.buildEvidencesMap(privacyByDesign),
    )
  }

  // O endpoint de resposta devolve a resposta atualizada (status, replier, risco,
  // evidências), então basta substituí-la no estado local — sem recarregar o projeto.
  const applyUpdatedAnswer = (questionId, updatedAnswer) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? { ...question, privacyByDesignAnswers: [updatedAnswer] }
          : question,
      ),
    )
    setEvidencesByAnswerId((previous) => ({
      ...previous,
      [updatedAnswer.id]: updatedAnswer.evidences || [],
    }))
  }

  const notifyError = (error, fallbackMessage) =>
    snackbar.open({
      message:
        helpers.formatters.errorMessage(error?.response?.data?.error) ||
        fallbackMessage,
      variant: 'error',
    })

  // Salva uma questão com os valores do formulário dela — resposta e anexos
  // novos no mesmo request.
  const saveAnswer = async (questionId, answerId, values) => {
    if (!answerId) return null

    setSaveState(questionId, SAVING_STATE)
    try {
      const response = await service.replyAnswer({
        privacyByDesignId,
        answerId,
        answer: values.answer,
        justify: values.justify,
        questionAlternativeId: values.questionAlternativeId || null,
        evidences: values.evidences,
      })

      const updatedAnswer = response?.data?.privacyByDesignAnswer
      if (updatedAnswer) applyUpdatedAnswer(questionId, updatedAnswer)

      setSaveState(questionId, SAVED_STATE)
      snackbar.open({
        message: 'Resposta salva com sucesso!',
        variant: 'success',
      })

      return updatedAnswer
    } catch (error) {
      setSaveState(questionId, null)
      notifyError(error, 'Erro ao salvar a resposta.')

      return null
    }
  }

  // Questão respondida é somente leitura: reabrir devolve a resposta para
  // pendente, mantendo o que já foi respondido, e libera a edição.
  const reopenAnswer = async (questionId, answerId) => {
    setSaveState(questionId, null)
    try {
      const response = await service.reopenAnswer({
        privacyByDesignId,
        answerId,
      })

      const updatedAnswer = response?.data?.privacyByDesignAnswer
      if (updatedAnswer) applyUpdatedAnswer(questionId, updatedAnswer)

      return updatedAnswer
    } catch (error) {
      notifyError(error, 'Erro ao reabrir a questão.')

      return null
    }
  }

  // Os endpoints de evidência recebem o id da resposta; o id da questão vem do
  // estado local.
  const removeEvidence = async (answerId, evidenceId) => {
    try {
      const response = await service.destroyAnswerEvidence({
        privacyByDesignId,
        answerId,
        evidenceId,
      })

      const updatedAnswer = response?.data?.privacyByDesignAnswer
      const question = questions.find(
        (item) => item.privacyByDesignAnswers?.[0]?.id === answerId,
      )
      if (updatedAnswer && question)
        applyUpdatedAnswer(question.id, updatedAnswer)

      return updatedAnswer
    } catch (error) {
      notifyError(error, 'Erro ao remover a evidência.')

      return null
    }
  }

  const areAllQuestionsAnswered =
    !isEmpty(questions) &&
    questions.every(
      (question) =>
        question.privacyByDesignAnswers?.[0]?.status === ANSWERED_STATUS,
    )

  // Trocar de projeto limpa os selos de salvamento da tela anterior.
  useEffect(() => {
    setSaveStateByQuestionId({})
  }, [privacyByDesignId])

  return {
    areAllQuestionsAnswered,
    evidencesByAnswerId,
    loadPrivacyByDesign,
    questions,
    removeEvidence,
    reopenAnswer,
    saveAnswer,
    saveStateByQuestionId,
  }
}

export default usePrivacyByDesignAnswers
